import { Component, Element, Event, EventEmitter, Host, Prop, h } from '@stencil/core';
import { AiIconType } from '../../utils/icon-types';

@Component({
  tag: 'biel-button',
  styleUrl: 'biel-button.css',
  shadow: true,
})
export class BielButton {
  bielBot: any;
  @Element() el: HTMLElement;
  // Define component properties
  @Prop() buttonPosition: string = 'default';
  @Prop() buttonStyle: string = 'default';
  @Prop() customFont: boolean = false;
  @Prop() hideIcon: boolean = false;
  @Prop() aiIcon: AiIconType = 'bot';
  @Prop() hideAvatars: boolean = false;
  @Prop() hideFeedback: boolean = false;
  @Prop() hideSources: boolean = false;

  // Define component events
  @Event() bielBotButtonClick: EventEmitter<void>;
  @Event() termsDeclined: EventEmitter<boolean>;

  // Define biel-bot properties
  @Prop() bielId: string = `biel-bot-${Math.random().toString(36).substring(2, 9)}`;
  @Prop() apiKey: string = '';
  @Prop() apiUrl: string = '';
  @Prop() disableInput: boolean = false;
  @Prop() email: string = '';
  @Prop() expandModal: boolean = false;
  @Prop() hideCloseButton: boolean = false;
  @Prop() hideExpandButton: boolean = false;
  @Prop() hideRefreshButton: boolean = false;
  @Prop() hideSettingsButton: boolean = false;
  @Prop() hideTooltips: boolean = false;
  @Prop() initialMessages: { text: string; sender: 'user' | 'ai'; feedback?: 0 | 1 }[] = [];
  @Prop() modalPosition: string = 'center';
  @Prop() project: string = '';
  @Prop() showTermsModal: boolean = false;
  @Prop() keepConversation: boolean = true;

  // Define biel-bot text properties
  @Prop() errorMessage403: string =
    'Oops! The request URL or API key is invalid or not authorized for this project.';
  @Prop() errorMessage404: string = 'Oops! We could not find the provided project ID.';
  @Prop() errorMessageDefault: string = 'Oops! Please try again later.';
  @Prop() footerText: string =
    'AI-generated answers may contain errors. Verify official sources before use.';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() inputPlaceholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() sourcesText: string = 'Sources';
  @Prop() suggestedQuestions: string = '';
  @Prop() suggestedQuestionsTitle: string = 'Suggested questions';
  @Prop() termsCheckboxText: string = 'I have read and agree to the Terms & Conditions.';
  @Prop() termsDescription: string =
    "Please review our <a href='https://biel.ai/terms' target='_blank' rel='noopener'>Terms & Conditions</a> before proceeding.";
  @Prop() termsTitle: string = 'Chatbot Terms & Conditions';
  @Prop() welcomeMessage: string = '';
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() settingsButtonText: string = 'Settings';
  @Prop() thinkModeEnabled: boolean = false;
  @Prop() assistantLabel: string = 'Assistant';

  connectedCallback() {
    this.bielBot = document.createElement('biel-bot');
    const props = [
      'bielId',
      'apiKey',
      'apiUrl',
      'disableInput',
      'email',
      'expandModal',
      'hideCloseButton',
      'hideExpandButton',
      'hideRefreshButton',
      'hideSettingsButton',
      'hideTooltips',
      'hideFeedback',
      'hideSources',
      'initialMessages',
      'modalPosition',
      'project',
      'showTermsModal',
      'errorMessage404',
      'errorMessage403',
      'errorMessageDefault',
      'footerText',
      'headerTitle',
      'inputPlaceholderText',
      'sendButtonText',
      'suggestedQuestionsTitle',
      'termsCheckboxText',
      'termsDescription',
      'termsTitle',
      'sourcesText',
      'suggestedQuestions',
      'welcomeMessage',
      'aiIcon',
      'hideAvatars',
      'keepConversation',
      'refreshButtonText',
      'expandButtonText',
      'collapseButtonText',
      'closeButtonText',
      'settingsButtonText',
      'thinkModeEnabled',
      'assistantLabel',
    ];

    props.forEach((prop) => {
      this.bielBot[prop] = this[prop];
    });

    document.body.appendChild(this.bielBot);
  }

  disconnectedCallback() {
    document.body.removeChild(this.bielBot);
  }

  isSafariBrowser() {
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
    return isSafari;
  }

  componentDidLoad() {
    const buttonContent = this.el.shadowRoot.querySelector('.biel-button-content') as HTMLElement;
    if (this.buttonPosition === 'center-right') {
      let adjustment = 0;
      if (this.isSafariBrowser()) {
        adjustment = 10;
      }
      buttonContent.style.right = `${((buttonContent.offsetWidth + adjustment) / 2) * -1}px`;
    }
    if (!this.customFont) {
      this.loadInterFont();
    }
  }

  loadInterFont() {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  showModal() {
    this.bielBot.showModal = true;
    this.bielBotButtonClick.emit();
    this.termsDeclined.emit(false);
  }

  render() {
    return (
      <Host>
        <a
          class={`biel-button-content biel-button-content--${this.buttonStyle} biel-button-content--${this.buttonPosition}`}
          onClick={() => this.showModal()}
        >
          {!this.hideIcon && (
            <span class="biel-button-content-icon">
              <biel-icon type={this.aiIcon} width="20" height="20"></biel-icon>
            </span>
          )}
          <slot />
        </a>
      </Host>
    );
  }
}
